import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'

import { LightTooltip, tooltipContent } from '../../../components/LightToolTip'

import { ESC_KEY, ENTER_KEY } from '../../constants'

import {
  setPageTitle,
  updatePageAsync,
  selectPageIsMine,
  selectPageTitle,
} from './pagesSlice'

const classes = (width) => ({
  root: {
    // The title is what gives way in a narrow window: it shrinks, cut short
    // with an ellipsis, so the buttons and links beside it stay on screen.
    display: 'flex',
    minWidth: '4em',
    flexShrink: 1,
    '& .pageTitle': {
      width: `${width}px`,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      border: '1px solid transparent',
      fontWeight: '900',
      fontSize: '1.8em',
      fontFamily: 'Roboto',
      backgroundColor: 'transparent',
      display: 'inline',
      color: 'white',
      marginRight: '1rem',
      '&:hover': {
        borderColor: '#666',
      },
      '&.editing, &:focus, &:focus-visible, &:active': {
        borderColor: '#AAA',
        outline: 'none',
        backgroundColor: '#444',
      },
      // The title of a page shared with me: the same text in the same place, but
      // plainly not a field. It never changes appearance — the rule above turns
      // the background dark on :active, which made a press look like it had
      // opened an editor — and it stays selectable, since the title is worth
      // copying.
      '&.static': {
        display: 'inline-block',
        cursor: 'text',
        userSelect: 'text',
        '&:hover, &:active, &:focus, &:focus-visible': {
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          outline: 'none',
        },
      },
    },
    '& .pageTitleSpan': {
      fontWeight: '900',
      fontSize: '1.8em',
      letterSpacing: 'normal',
      border: '1px solid transparent',
      padding: '2px',
      position: 'fixed',
      backgroundColor: 'blue',
      left: 0,
      top: -100,
    },
  },
})

function PageTitleContainer(props) {
  const pageId = props.pageId
  const pageTitle = useSelector(selectPageTitle(pageId))
  // A page shared with me is read-only, so its title isn't mine to rename.
  const isMine = useSelector(selectPageIsMine(pageId))

  const dispatch = useDispatch()

  const domRef = useRef(null)
  const spanRef = useRef(null)

  // useEffect(() => {
  //   domRef?.current?.focus()
  // }, [pageId])

  const [title, setTitle] = useState(pageTitle)
  const [isEditing, setIsEditing] = useState(false)
  const [width, setWidth] = useState(100)

  const saveTitleChange = () => {}

  const handleInputClick = (e) => {
    // console.log('handleInputClick()')
    setIsEditing(true)
  }

  const handleInputKeyDown = (e) => {
    const keyCode = e.keyCode

    if (keyCode === ESC_KEY) {
      // Reset to what it was before
      setTitle(pageTitle)
      spanRef.current.blur()
    } else if (keyCode === ENTER_KEY) {
      dispatch(setPageTitle({ pageId, title }))
      dispatch(updatePageAsync(pageId))
      setIsEditing(false)
      domRef.current.blur()
    }
    // console.log('handleInputKeyDown() e.keyCode:', e.keyCode)
  }

  const handleInputChange = (e) => {
    setTitle(e.target.value)
    // domRef.current.blur()
  }

  const handleInputBlur = (e) => {
    dispatch(setPageTitle({ pageId, title }))
    dispatch(updatePageAsync(pageId))
    setIsEditing(false)
  }

  useEffect(() => {
    const spanWidth = spanRef.current.getBoundingClientRect().width
    // console.log('useEffect() width now is:', w)
    // Add a bit extra to take account of the space at the end
    setWidth(spanWidth + 5.375)
  })

  return (
    <Box component='span' sx={classes(width).root}>
      {isMine ? (
        <LightTooltip title={tooltipContent('Rename', <EditIcon />)}>
          <input
            className={`pageTitle ${isEditing ? 'editing' : ''}`}
            ref={domRef}
            type='text'
            value={title}
            onClick={handleInputClick}
            onKeyDown={handleInputKeyDown}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          ></input>
        </LightTooltip>
      ) : (
        // Someone else's page: the title is a label, not a field. A read-only
        // input still took focus, showed a caret and turned grey when clicked,
        // which looked editable, so this isn't an input at all.
        <Box component='span' className='pageTitle static' ref={domRef}>
          {title}
        </Box>
      )}
      <div className='pageTitleSpan' ref={spanRef}>
        {title}
      </div>
    </Box>
  )
}

export default PageTitleContainer
